/**
 * RuleCheckSheet — the bowler warning sheet (rulebook phase 5), also used for
 * batter picks.
 *
 * The server refuses a pick that breaks a selection rule with 409
 * {code: 'rule_check', blocks, warnings}. Warnings (top order can't bowl, last
 * match's openers, a category in the top N) can be gone ahead with — the
 * rule's runs penalty then applies itself. Blocks (overs per bowler, different
 * bowlers) need the umpire to allow them, with a reason.
 */
import { useState } from 'react';
import RuleSheet from './RuleSheet';

export const isRuleCheck = (e) => e?.response?.status === 409 && e?.response?.data?.detail?.code === 'rule_check';

export default function RuleCheckSheet({ check, onCancel, onConfirm, busy }) {
  const [reason, setReason] = useState('');
  if (!check) return null;
  const { blocks = [], warnings = [] } = check;
  const penalty = warnings.reduce((n, w) => n + (w.penalty_runs || 0), 0);
  const go = () => onConfirm({
    accept_rules: warnings.map((w) => w.rule_key),
    umpire_reason: blocks.length ? reason.trim() : undefined,
  });
  return (
    <RuleSheet open onClose={onCancel} testId="rule-check-sheet"
      title={blocks.length ? "This pick isn't allowed" : 'This breaks a rule'}
      subtitle="From the event's rulebook"
      footer={(
        <div className="flex gap-2">
          <button type="button" onClick={onCancel} className="flex-1 h-12 rounded-xl border border-[#2B3A67] text-[#2B3A67] font-bold"
            data-testid="rule-check-cancel">Pick another</button>
          <button type="button" onClick={go} disabled={busy || (blocks.length > 0 && !reason.trim())}
            className="flex-[1.3] h-12 rounded-xl bg-[#E54562] text-white font-bold disabled:opacity-40" data-testid="rule-check-go">
            {blocks.length ? 'Umpire allows it' : 'Go ahead'}{penalty ? ` (−${penalty} runs)` : ''}
          </button>
        </div>
      )}>
      <ul className="space-y-2">
        {blocks.map((b, i) => (
          <li key={`b${i}`} className="rounded-xl border-l-4 border-[#E54562] bg-[#FCE8EC] px-3 py-2 text-sm text-[#2B3A67]">
            <i className="ph-bold ph-prohibit text-[#B3263F]" /> {b.text}
          </li>
        ))}
        {warnings.map((w, i) => (
          <li key={`w${i}`} className="rounded-xl border-l-4 border-[#F4D341] bg-[#FDF5D3] px-3 py-2 text-sm text-[#2B3A67]">
            <i className="ph-bold ph-warning" /> {w.text}
            {w.penalty_runs ? <span className="block text-xs font-bold mt-0.5">Going ahead costs {w.penalty_runs} runs</span> : null}
          </li>
        ))}
      </ul>
      {blocks.length > 0 && (
        <div className="mt-4">
          <p className="text-xs font-bold text-gray-500 mb-1.5">Umpire's reason</p>
          <input value={reason} onChange={(e) => setReason(e.target.value)} maxLength={200}
            placeholder="e.g. bowler injured, no one else can bowl"
            className="w-full h-11 px-3 rounded-xl border border-gray-200 text-sm" data-testid="rule-check-reason" />
          <p className="text-[11px] text-gray-500 mt-1">Recorded in the match history.</p>
        </div>
      )}
    </RuleSheet>
  );
}
